/**
 * Ditado da descrição da operação: grava o microfone, converte para WAV mono
 * de 16 kHz e manda para a transcrição do servidor (OpenRouter).
 *
 * O WAV é o formato que todo provedor de transcrição aceita — o webm/ogg do
 * MediaRecorder varia por navegador. Em 16 kHz mono, os 90 s de teto cabem com
 * folga no limite de upload da API.
 */

import { Loader2, Mic, Square } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useTranscrever } from "@/hooks/queries";

const TAXA_AMOSTRAGEM = 16000;
const DURACAO_MAXIMA_MS = 90_000;
/** Barras da onda sonora — ímpar, para ter uma barra central. */
const BARRAS = 9;

interface Props {
  onTexto: (texto: string) => void;
  disabled?: boolean;
}

export function BotaoMicrofone({ onTexto, disabled }: Props) {
  const transcrever = useTranscrever();
  const [gravando, setGravando] = React.useState(false);
  const [fluxoAtivo, setFluxoAtivo] = React.useState<MediaStream | null>(null);
  const gravador = React.useRef<MediaRecorder | null>(null);
  const limite = React.useRef<number | undefined>(undefined);

  // Solta o microfone se o componente sair da tela no meio da gravação.
  React.useEffect(
    () => () => {
      window.clearTimeout(limite.current);
      if (gravador.current?.state === "recording") gravador.current.stop();
    },
    [],
  );

  const enviar = async (partes: Blob[]) => {
    try {
      const wav = await paraWav(new Blob(partes));
      transcrever.mutate(
        { audio: await paraBase64(wav), formato: "wav" },
        {
          onSuccess: ({ texto }) => onTexto(texto),
          onError: (erro: Error) => toast.error(erro.message),
        },
      );
    } catch {
      toast.error("Não consegui processar o áudio gravado.");
    }
  };

  const iniciar = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Este navegador não permite gravar áudio.");
      return;
    }
    let fluxo: MediaStream;
    try {
      fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Sem acesso ao microfone — libere a permissão no navegador.");
      return;
    }

    const partes: Blob[] = [];
    const rec = new MediaRecorder(fluxo);
    rec.ondataavailable = (e) => e.data.size && partes.push(e.data);
    rec.onstop = () => {
      window.clearTimeout(limite.current);
      fluxo.getTracks().forEach((t) => t.stop());
      setGravando(false);
      setFluxoAtivo(null);
      if (partes.length) void enviar(partes);
    };
    rec.start();
    gravador.current = rec;
    setGravando(true);
    setFluxoAtivo(fluxo);
    limite.current = window.setTimeout(
      () => rec.state === "recording" && rec.stop(),
      DURACAO_MAXIMA_MS,
    );
  };

  const parar = () =>
    gravador.current?.state === "recording" && gravador.current.stop();

  const ocupado = transcrever.isPending;

  return (
    <Button
      type="button"
      variant={gravando ? "destructive" : "outline"}
      onClick={gravando ? parar : iniciar}
      disabled={ocupado || (disabled && !gravando)}
      title={gravando ? "Parar e transcrever" : "Ditar a descrição"}
      aria-label={gravando ? "Parar e transcrever" : "Ditar a descrição"}
    >
      {ocupado ? (
        <Loader2 className="animate-spin" />
      ) : gravando ? (
        <>
          <Square className="!size-3 fill-current" />
          {fluxoAtivo && <OndaSonora fluxo={fluxoAtivo} />}
        </>
      ) : (
        <Mic />
      )}
    </Button>
  );
}

/**
 * Barras que dançam com a voz: um `AnalyserNode` lê o espectro do microfone e
 * cada quadro redimensiona as barras direto no DOM (sem re-render do React).
 * As frequências graves ficam no centro e as agudas nas pontas, em espelho, e
 * uma ondulação leve mantém a onda viva mesmo no silêncio.
 */
function OndaSonora({ fluxo }: { fluxo: MediaStream }) {
  const barras = React.useRef<(HTMLSpanElement | null)[]>([]);

  React.useEffect(() => {
    const ctx = new AudioContext();
    const analisador = ctx.createAnalyser();
    analisador.fftSize = 256;
    analisador.smoothingTimeConstant = 0.75;
    ctx.createMediaStreamSource(fluxo).connect(analisador);
    const espectro = new Uint8Array(analisador.frequencyBinCount);
    const centro = (BARRAS - 1) / 2;

    let quadro = 0;
    const desenhar = (t: number) => {
      analisador.getByteFrequencyData(espectro);
      barras.current.forEach((barra, i) => {
        if (!barra) return;
        const distancia = Math.abs(i - centro);
        // Faixa da voz: ~190 Hz a ~3 kHz com fftSize 256 a 48 kHz.
        const nivel = (espectro[1 + distancia * 3] ?? 0) / 255;
        const ondulacao = 0.08 * (1 + Math.sin(t / 180 + i * 0.9));
        const altura = Math.min(1, 0.18 + ondulacao + nivel * (1 - distancia * 0.12));
        barra.style.transform = `scaleY(${altura})`;
      });
      quadro = requestAnimationFrame(desenhar);
    };
    quadro = requestAnimationFrame(desenhar);

    return () => {
      cancelAnimationFrame(quadro);
      void ctx.close();
    };
  }, [fluxo]);

  return (
    <span aria-hidden className="flex h-5 items-center gap-[3px]">
      {Array.from({ length: BARRAS }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            barras.current[i] = el;
          }}
          className="h-full w-[3px] origin-center scale-y-[0.2] rounded-full bg-current transition-transform duration-75 ease-out"
        />
      ))}
    </span>
  );
}

/** Decodifica o que o navegador gravou e reamostra para PCM 16 bits mono 16 kHz. */
async function paraWav(audio: Blob): Promise<Blob> {
  const ctx = new AudioContext();
  try {
    const original = await ctx.decodeAudioData(await audio.arrayBuffer());
    const quadros = Math.ceil(original.duration * TAXA_AMOSTRAGEM);
    const offline = new OfflineAudioContext(1, quadros, TAXA_AMOSTRAGEM);
    const fonte = offline.createBufferSource();
    fonte.buffer = original;
    fonte.connect(offline.destination);
    fonte.start();
    const amostras = (await offline.startRendering()).getChannelData(0);

    const buffer = new ArrayBuffer(44 + amostras.length * 2);
    const v = new DataView(buffer);
    const texto = (pos: number, s: string) =>
      [...s].forEach((c, i) => v.setUint8(pos + i, c.charCodeAt(0)));
    texto(0, "RIFF");
    v.setUint32(4, 36 + amostras.length * 2, true);
    texto(8, "WAVE");
    texto(12, "fmt ");
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); // PCM
    v.setUint16(22, 1, true); // mono
    v.setUint32(24, TAXA_AMOSTRAGEM, true);
    v.setUint32(28, TAXA_AMOSTRAGEM * 2, true);
    v.setUint16(32, 2, true);
    v.setUint16(34, 16, true);
    texto(36, "data");
    v.setUint32(40, amostras.length * 2, true);
    amostras.forEach((a, i) => {
      const s = Math.max(-1, Math.min(1, a));
      v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    });
    return new Blob([buffer], { type: "audio/wav" });
  } finally {
    void ctx.close();
  }
}

function paraBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result).split(",")[1] ?? "");
    leitor.onerror = () => reject(leitor.error);
    leitor.readAsDataURL(blob);
  });
}
